<template>
  <div class="overlay">
    <div class="loading-container">
      <div class="loading-spinner" />
      <p class="loading-spinner-message">{{ message.message }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue';

const props = withDefaults(defineProps<{
  tags?: string[];
  includeDefaultMessages?: boolean;
}>(), {
    includeDefaultMessages: true,
  },
);

type Message = {
  message: string;
  tags?: string[];
};

const messages: Message[] = [
  // General loading messages
  {
    message: 'Loading...',
  },
  {
    message: 'Still working on it…',
  },
  {
    message: 'This might take a few seconds.',
  },
  {
    message: 'Almost there — thanks for your patience.',
  },
  {
    message: 'Optimising performance…',
  },

  // Data-related messages
  {
    message: 'Crunching the numbers…',
    tags: ['data'],
  },
  {
    message: 'Compiling insights…',
    tags: ['data'],
  },
  {
    message: 'Fetching your data, just a moment.',
    tags: ['data'],
  },
  {
    message: 'Loading the latest results…',
    tags: ['data'],
  },
  {
    message: 'Downloading Vantage Echo visualisation…',
    tags: ['echo'],
  },
];

const message = ref<Message>(messages[0]);

const getRandomMessage = () => {
  // If includeDefaultMessages is true, always include general messages (those without tags)
  const filteredMessages: Message[] = props.includeDefaultMessages ? messages.filter((message) => !message.tags) : [];

  // If tags are provided, also include messages that match those tags
  if (props.tags && props.tags.length > 0) {
    filteredMessages.push(
      ...messages.filter((message) => message.tags?.some((tag) => props.tags?.includes(tag))),
    );
  }

  // Ensure we always have at least one message to return
  if (filteredMessages.length === 0) {
    return messages[0];
  }

  return filteredMessages[Math.floor(Math.random() * filteredMessages.length)];
};

let messageInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  document.body.classList.add('overlay-open');

  // Set initial message
  message.value = getRandomMessage();

  // Rotate messages every 3 seconds
  messageInterval = setInterval(() => {
    message.value = getRandomMessage();
  }, 3000);
});

onBeforeUnmount(() => {
  document.body.classList.remove('overlay-open');
  if (messageInterval) {
    clearInterval(messageInterval);
  }
});
</script>

<style lang="scss">
.overlay-open {
  min-height: 100%;
  overflow: hidden;
}

.overlay {
  @include mixins.flex-center();
  background-color: rgba(0, 0, 0, 0.4);
  height: 100%;
  left: 0px;
  pointer-events: auto;
  position: fixed;
  top: 0px;
  width: 100%;
  z-index: 1101;
}

.loading-container {
  @include mixins.flex-center();
  flex-direction: column;
  gap: variables.$sizing-lg;
}

.loading-spinner-message {
  @include typography.font-body($weight: normal);
  color: variables.$primitives-color-background-100;
  text-align: center;
  z-index: 2102;
  margin: 0;
  margin-top: variables.$sizing-lg;
}
</style>
